/**
 * Step 2: Period Configuration
 *
 * Configure contribution period and C10 format type.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import { useWizard } from '../../context/WizardContext';
import { WizardNavigation } from '../WizardNavigation';
import type { FormatType } from '../../context/types';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Month names for dropdown.
 */
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Step 2: Configure contribution period and format.
 */
export function Step2Period() {
  const { state, setPeriod, setFormatType, nextStep } = useWizard();
  const { user } = useAuth();

  // Generate year options (current year and previous 2)
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPeriod(parseInt(e.target.value, 10), state.contributionYear);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPeriod(state.contributionMonth, parseInt(e.target.value, 10));
  };

  const handleFormatChange = (format: FormatType) => {
    setFormatType(format);
  };

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-6">
        Configure Contribution Period
      </h2>

      {/* Business info (if available) */}
      {user && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Business Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">User:</span>{' '}
              <span className="text-gray-900">{user.email}</span>
            </div>
            {user.company_name && (
              <div>
                <span className="text-gray-500">Company:</span>{' '}
                <span className="text-gray-900">{user.company_name}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Period selection */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div>
          <label
            htmlFor="month"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contribution Month
          </label>
          <select
            id="month"
            value={state.contributionMonth}
            onChange={handleMonthChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="year"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contribution Year
          </label>
          <select
            id="year"
            value={state.contributionYear}
            onChange={handleYearChange}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Format selection */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          C10 Format Type
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Non-Hospitality */}
          <button
            onClick={() => handleFormatChange('NON_HOSPITALITY')}
            type="button"
            className={`
              relative p-4 rounded-lg border-2 text-left transition-colors
              ${
                state.formatType === 'NON_HOSPITALITY'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }
            `}
          >
            <h4 className="text-sm font-medium text-gray-900">
              Standard (Non-Hospitality)
            </h4>
            <p className="mt-1 text-xs text-gray-500">
              79-byte format for most businesses. No gratuity tracking.
            </p>
            {state.formatType === 'NON_HOSPITALITY' && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-3 h-3 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </button>

          {/* Hospitality */}
          <button
            onClick={() => handleFormatChange('HOSPITALITY')}
            type="button"
            className={`
              relative p-4 rounded-lg border-2 text-left transition-colors
              ${
                state.formatType === 'HOSPITALITY'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }
            `}
          >
            <h4 className="text-sm font-medium text-gray-900">Hospitality</h4>
            <p className="mt-1 text-xs text-gray-500">
              115-byte format for hotels, restaurants, tourism. Includes
              gratuity fields.
            </p>
            {state.formatType === 'HOSPITALITY' && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-3 h-3 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Employees summary (if already parsed) */}
      {state.employees.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Employees to Process
          </h3>
          <p className="text-sm text-gray-600">
            {state.employees.length} employees from{' '}
            {state.sourceType === 'quickbooks'
              ? 'QuickBooks'
              : state.sourceType === 'csv'
                ? 'CSV file'
                : 'manual entry'}
          </p>
        </div>
      )}

      {/* Period summary */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          You are creating a C10 for{' '}
          <strong>
            {MONTHS[state.contributionMonth - 1]} {state.contributionYear}
          </strong>{' '}
          using the{' '}
          <strong>
            {state.formatType === 'HOSPITALITY' ? 'Hospitality' : 'Standard'}
          </strong>{' '}
          format.
        </p>
      </div>

      {/* Navigation */}
      <WizardNavigation onNext={nextStep} />
    </div>
  );
}
